/* eslint-disable no-console */
import { NavigateFunction } from "react-router-dom";
import { AxiosError } from "axios";
import { getCookie } from "./getCookie";
import { getRefreshTokenWhenConcurrentAPICalls } from "./getRefreshTokenWhenConcurrentAPICalls";
import { useAppStore } from "../../appStore";
import { logger } from "../../utils/logger";

// Safety timeout duration for clearing the refresh flag (in milliseconds)
const REFRESH_FLAG_TIMEOUT = 15000; // 15 seconds
// Delay before retrying if another refresh is in progress (in milliseconds)
const RETRY_DELAY_MS = 1500; // 1.5 seconds

/** Returns the response body as a string for safe .includes() checks. */
function getResponseBodyString(reason: AxiosError): string {
  try {
    const data = reason.response?.data;
    if (data !== undefined && data !== null) {
      return typeof data === "string" ? data : JSON.stringify(data);
    }
    const req = reason.request as { response?: string } | undefined;
    return req?.response ?? "";
  } catch {
    return "";
  }
}

export const concurrentAPIErrorHandler = (
  reason: AxiosError,
  navigate: NavigateFunction,
  retryFunction: any,
  errorHandlingFunction: any
) => {
  const token = getCookie("token");
  const refreshToken = getCookie("token_secret");

  // Get store state and actions
  const refreshingToken = useAppStore.getState().refreshingToken;
  const setRefreshingToken = useAppStore.getState().setRefreshingToken;

  // Log the error for debugging
  logger.error("API Error:", reason.message);

  // --- Handle 403 token errors BEFORE offline check ---
  if (reason.response?.status === 403) {
    const body = getResponseBodyString(reason);
    const isTokenErrorBody =
      body.includes("SocialToken matching query does not exist") ||
      body.includes("Returned invalid token from cache") ||
      body.includes("Token is expired") ||
      body.includes("Token not found") ||
      Boolean(!token && refreshToken) === true;

    if (isTokenErrorBody) {
      if (refreshingToken) {
        logger.log("Token refresh already in progress...");
        return;
      }
      logger.log("Initiating token refresh (403)...");
      setRefreshingToken(true);
      getRefreshTokenWhenConcurrentAPICalls(navigate, retryFunction);
      setTimeout(() => {
        if (useAppStore.getState().refreshingToken) {
          logger.warn("Token refresh flag (403) cleanup timeout reached.");
          setRefreshingToken(false);
        }
      }, REFRESH_FLAG_TIMEOUT);
      return;
    }
  }

  // --- Check browser online status (after 403 token handling) ---
  if (!navigator.onLine) {
    logger.log("Browser is offline. Redirecting to /no-internet");
    if (window.location.pathname !== "/no-internet") {
      navigate("/no-internet", { replace: true });
    }
    if (errorHandlingFunction) {
      errorHandlingFunction();
    }
    return;
  }

  if (reason.response) {
    const status = reason.response.status;

    if (status === 403) {
      logger.log("Network Error - Connection issue detected");

      // --- Specific Token Error Handling ---
      const body = getResponseBodyString(reason);
      if (
        body.includes("SocialToken matching query does not exist") ||
        body.includes("Returned invalid token from cache") ||
        body.includes("Token is expired") ||
        body.includes("Token not found") ||
        Boolean(!token && refreshToken) === true
      ) {
        // Check if refresh is already in progress
        if (refreshingToken) {
          logger.log("Token refresh already in progress...");
          // setTimeout(retryFunction, RETRY_DELAY_MS); // Wait and retry
          return;
        }

        // Initiate refresh
        logger.log("Initiating token refresh (403)...");
        setRefreshingToken(true);
        getRefreshTokenWhenConcurrentAPICalls(navigate, retryFunction);

        // Safety timeout to clear the flag
        setTimeout(() => {
          if (useAppStore.getState().refreshingToken) {
            logger.warn("Token refresh flag (403) cleanup timeout reached.");
            setRefreshingToken(false);
          }
        }, REFRESH_FLAG_TIMEOUT);
        return; // Stop processing this error further in this handler
      }
      // --- End Specific Token Error Handling ---

      // Implement retry logic for transient network issues (non-token 403s)
      const retryCount = parseInt(
        localStorage.getItem("networkErrorRetryCount") || "0"
      );
      if (retryCount < 3) {
        localStorage.setItem(
          "networkErrorRetryCount",
          (retryCount + 1).toString()
        );
        logger.log(`Retrying request (${retryCount + 1}/3)...`);
        setTimeout(() => {
          retryFunction();
        }, 1000 * Math.pow(2, retryCount));
        return;
      }

      // Reset retry counter after max attempts
      localStorage.setItem("networkErrorRetryCount", "0");

      // Only redirect to no-internet when the browser is actually offline (!navigator.onLine).
      // Do not redirect for 403 or empty response — those are auth/server issues, not connectivity.
      if (errorHandlingFunction) {
        errorHandlingFunction();
      }
    } else if (status === 401) {
      // Unauthorized - token issues
      // Check if refresh is already in progress
      if (refreshingToken) {
        logger.log("Token refresh already in progress, retrying shortly...");
        setTimeout(retryFunction, RETRY_DELAY_MS); // Wait and retry
        return;
      }

      // Initiate refresh
      logger.log("Initiating token refresh (401)...");
      setRefreshingToken(true);
      getRefreshTokenWhenConcurrentAPICalls(navigate, retryFunction);

      // Safety timeout to clear the flag
      setTimeout(() => {
        if (useAppStore.getState().refreshingToken) {
          logger.warn("Token refresh flag (401) cleanup timeout reached.");
          setRefreshingToken(false);
        }
      }, REFRESH_FLAG_TIMEOUT);
      return; // Stop processing this error further in this handler
    } else if (status >= 500) {
      // Server errors - don't retry, just handle the error
      if (errorHandlingFunction) {
        errorHandlingFunction();
      }
      return;
    } else if (status === 404) {
      // Resource not found
      if (errorHandlingFunction) {
        errorHandlingFunction();
      }
    } else if (status === 429) {
      // Rate limiting
      logger.log("429 - Too many requests. Retrying after: 30 seconds");
      setTimeout(() => {
        retryFunction();
      }, 30000);
      if (errorHandlingFunction) {
        errorHandlingFunction();
      }
    } else {
      // Handle other status codes
      if (
        reason.response?.data &&
        typeof reason.response.data === "object" &&
        "group" in reason.response.data
      ) {
        logger.log(
          `Error: ${(reason.response.data as { group: string[] }).group?.[0]}`
        );
      } else {
        logger.log(
          "Error: Unable to retrieve specific error details. Running error handling functionality."
        );
      }
      if (errorHandlingFunction) {
        errorHandlingFunction();
      }
    }
  } else {
    // Fallback for non-response errors (e.g., request setup, network timeout before response)
    logger.log(`Unhandled Error: ${reason}`);
    if (errorHandlingFunction) {
      errorHandlingFunction();
    }
  }
};
